import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2, Copy, Check, Share2, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";
import { joinWaitlist, getWaitlistCount, shareUrlFor } from "@/lib/api";

// Corridor pill inference — maps `${from}-${to}` currency pair to the
// matching corridor label used on the button-group above.
const CCY_CORRIDOR = {
  "GBP-NGN": "UK → NG",
  "USD-GHS": "US → GH", "EUR-GHS": "US → GH",
  "GBP-KES": "EU → KE", "EUR-KES": "EU → KE",
  "USD-NGN": "US → GH",
  "CAD-ZAR": "CA → ZA",
  "AUD-UGX": "AU → UG",
};

const CCY_SYMBOL = {
  USD: "$", GBP: "£", EUR: "€", CAD: "C$", AUD: "A$", CHF: "Fr",
  NGN: "₦", KES: "KSh", GHS: "GH₵", XOF: "CFA", ZAR: "R",
  UGX: "USh", MAD: "MAD", EGP: "E£", TZS: "TSh",
};

export default function WaitlistCTA({ compact = false }) {
  const { t } = useTranslation();
  const { prefix } = useLang();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [corridor, setCorridor] = useState("");
  const [status, setStatus] = useState("idle"); // idle|loading|done|error
  const [error, setError] = useState("");
  const [entry, setEntry] = useState(null);
  const [referrerCode, setReferrerCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [seats, setSeats] = useState({
    founder_seats_taken: 0,
    founder_seats_total: 100,
    founder_seats_left: 100,
  });
  const [pulse, setPulse] = useState(false);
  const [handoff, setHandoff] = useState(null); // { amount, from, to, receive }
  const [source, setSource] = useState("");

  // Capture ?ref= on mount + persist
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("ref");
      if (fromUrl) {
        localStorage.setItem("instant.ref", fromUrl);
        setReferrerCode(fromUrl.toUpperCase());
      } else {
        const stored = localStorage.getItem("instant.ref");
        if (stored) setReferrerCode(stored.toUpperCase());
      }

      // Widget-to-Waitlist handoff — pre-fill corridor + show quote banner
      const send = params.get("send");
      const fromCcy = params.get("from");
      const toCcy = params.get("to");
      const recv = params.get("receive");
      if (send && fromCcy && toCcy) {
        setHandoff({
          amount: Number(send) || 0,
          from: fromCcy.toUpperCase(),
          to: toCcy.toUpperCase(),
          receive: Number(recv) || 0,
        });
        const key = `${fromCcy.toUpperCase()}-${toCcy.toUpperCase()}`;
        const matched = CCY_CORRIDOR[key] || "Other";
        setCorridor(matched);
      }

      // Attribution source — where the visitor came from (hero-quote, corridor-detail, ...)
      const src = params.get("source");
      if (src) setSource(src.trim().slice(0, 64));
      else if (send && fromCcy && toCcy) {
        // Sensible default if a handoff arrived without an explicit source tag
        setSource(`quote-${fromCcy.toLowerCase()}-${toCcy.toLowerCase()}`);
      }
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    // Initial count via REST — fast first paint.
    getWaitlistCount()
      .then((c) => setSeats(c))
      .catch(() => {});

    // Then upgrade to a live WebSocket for real-time seat updates.
    let ws = null;
    let closed = false;
    let retry = null;
    const connect = () => {
      if (closed) return;
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const wsUrl = base.replace(/^http/, "ws") + "/api/ws/waitlist";
        ws = new WebSocket(wsUrl);
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg?.type === "count") {
              setSeats((prev) => {
                if (msg.count > (prev.count || prev.founder_seats_taken || 0)) {
                  // Pulse the seat tile briefly on any live increment.
                  setPulse(true);
                  setTimeout(() => setPulse(false), 1200);
                }
                return msg;
              });
            }
          } catch (_) { /* ignore */ }
        };
        ws.onclose = () => {
          if (!closed) {
            retry = setTimeout(connect, 8000);  // reconnect with backoff
          }
        };
        ws.onerror = () => { try { ws.close(); } catch (_) { /* noop */ } };
      } catch (_) { /* silent — REST fallback stays */ }
    };
    connect();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      if (ws) try { ws.close(); } catch (_) { /* noop */ }
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError(t("waitlist.invalidEmail"));
      return;
    }
    setStatus("loading");
    try {
      const res = await joinWaitlist({
        email,
        full_name: name || null,
        corridor: corridor || null,
        referrer_code: referrerCode || null,
        source: source || null,
      });
      setEntry(res);
      setStatus("done");
      // Fire a handoff-success analytics event so we can measure the
      // Hero-quote / Corridor-page → Founder-seat funnel in one query.
      if (source || handoff) {
        try {
          const meta = {
            handoff: !!handoff,
            is_founder: !!res?.is_founder,
            seat: res?.seat || null,
            corridor: corridor || null,
            from: handoff?.from || null,
            to: handoff?.to || null,
            amount: handoff?.amount || null,
          };
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/event`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: "handoff_signup",
              source: source || null,
              target: "waitlist",
              meta,
            }),
          }).catch(() => {});
        } catch (_) { /* silent */ }
      }
      // refresh seat counter
      getWaitlistCount().then(setSeats).catch(() => {});
    } catch (err) {
      setStatus("error");
      setError(t("waitlist.error"));
    }
  };

  const shareLink = entry?.referral_code ? shareUrlFor(entry.referral_code) : "";

  const copyLink = () => {
    if (!shareLink) return;
    navigator.clipboard?.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const nativeShare = async () => {
    if (!shareLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on Instantxfer CashApp",
          text: "I just got a Founder seat on Instantxfer CashApp. Take mine and skip the queue —",
          url: shareLink,
        });
      } catch { /* user cancelled share */ }
    } else {
      copyLink();
    }
  };

  const pct = Math.min(
    100,
    Math.round((seats.founder_seats_taken / (seats.founder_seats_total || 100)) * 100)
  );

  return (
    <section
      className={`py-24 md:py-32 bg-canvas ${compact ? "" : ""}`}
      data-testid="waitlist-cta"
      id="waitlist"
    >
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="label-eyebrow">{t("waitlist.privateBeta")}</div>
            <h2 className="mt-5 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
              {t("waitlist.skipQueue1")}<br />
              <span className="italic font-serif text-forest/80">{t("waitlist.skipQueue2Italic")}</span>
            </h2>
            <p className="mt-6 max-w-lg text-ink/70 leading-relaxed">
              {t("waitlist.membersLine")}
            </p>

            {/* Founder wall */}
            <div
              className={
                "mt-8 rounded-2xl border border-forest/15 bg-white p-5 max-w-lg transition-all duration-500 " +
                (pulse ? "border-lime/60 shadow-[0_0_28px_-6px_rgba(232,255,0,0.55)] bg-lime/[0.06]" : "")
              }
              data-testid="founder-wall"
              data-pulse={pulse ? "on" : "off"}
            >
              <div className="flex items-baseline justify-between">
                <div className="label-eyebrow">{t("waitlist.founderLive")}</div>
                <div className="font-display text-forest text-lg tracking-tight">
                  <span data-testid="founder-seats-taken">{seats.founder_seats_taken}</span>
                  <span className="text-ink/40"> / {seats.founder_seats_total} {t("waitlist.seatsTaken")}</span>
                </div>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-forest/10 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
                  className="h-full bg-forest"
                />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-ink/55">
                <span className="inline-flex items-center gap-2">
                  <span className="live-dot" /> {t("waitlist.seatsFillLive")}
                </span>
                <span data-testid="founder-seats-left">
                  {seats.founder_seats_left} {t("waitlist.seatsLeft")}
                </span>
              </div>
            </div>

            {referrerCode && status !== "done" && (
              <div
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-lime text-ink px-4 py-2 text-sm font-semibold"
                data-testid="referrer-badge"
              >
                <Share2 className="w-4 h-4" />
                {t("waitlist.referredBy")} <span className="font-mono ml-1">{referrerCode}</span> {t("waitlist.skipQueueTag")}
              </div>
            )}
          </div>

          <div className="lg:col-span-6">
            <motion.form
              onSubmit={submit}
              noValidate
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
              className="card-flat p-6 md:p-8 relative overflow-hidden"
              data-testid="waitlist-form"
            >
              {handoff && status !== "done" && (
                <div
                  className="mb-5 rounded-2xl bg-lime text-ink px-4 py-3 flex items-start gap-3"
                  data-testid="waitlist-handoff-banner"
                >
                  <Sparkles className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2} />
                  <div className="text-sm leading-snug">
                    <div className="font-semibold">
                      {t("waitlist.quoteLabel")} {CCY_SYMBOL[handoff.from] || ""}{handoff.amount.toLocaleString()} {handoff.from} → {CCY_SYMBOL[handoff.to] || ""}{Math.round(handoff.receive).toLocaleString()} {handoff.to}
                    </div>
                    <div className="text-ink/70 text-xs mt-0.5">
                      {t("waitlist.quoteCta")}
                    </div>
                  </div>
                </div>
              )}

              {!handoff && !source && status !== "done" && (
                <a
                  href="/#send-widget"
                  className="mb-5 rounded-2xl border border-forest/12 bg-surface px-4 py-3 flex items-start gap-3 hover:bg-lime/10 transition-colors group"
                  data-testid="waitlist-source-nudge"
                >
                  <span className="w-8 h-8 rounded-full bg-lime text-ink grid place-items-center shrink-0 text-sm">
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                  </span>
                  <div className="text-sm leading-snug">
                    <div className="font-semibold text-forest">
                      {t("waitlist.nudgeTitle")}
                    </div>
                    <div className="text-ink/60 text-xs mt-0.5">
                      {t("waitlist.nudgeBody")}
                    </div>
                  </div>
                </a>
              )}
              {status === "done" ? (
                <div className="flex flex-col items-start gap-4 py-4" data-testid="waitlist-success">
                  {entry?.is_founder && entry?.seat ? (
                    <div className="w-full rounded-2xl bg-forest text-canvas p-6 relative overflow-hidden" data-testid="founder-badge">
                      <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-lime/20 blur-2xl" />
                      <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-lime">
                        Founder 100 · Seat reserved
                      </div>
                      <div className="mt-3 font-serif italic text-2xl text-canvas/85">Seat No.</div>
                      <div
                        className="font-display font-medium text-6xl text-lime tracking-tightest leading-none"
                        data-testid="founder-seat"
                      >
                        {String(entry.seat).padStart(3, "0")}
                      </div>
                      <div className="mt-2 text-canvas/60 text-xs tracking-[0.22em] uppercase">
                        of 100 · non-transferable
                      </div>
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-lime grid place-items-center">
                      <CheckCircle2 className="w-6 h-6 text-forest" />
                    </div>
                  )}
                  <h3 className="font-display text-3xl text-forest tracking-tightest">
                    {entry?.is_founder ? t("waitlist.founderTitle") : t("waitlist.successTitle")}
                  </h3>
                  <p className="text-ink/70">
                    {entry?.is_founder
                      ? t("waitlist.founderBody")
                      : t("waitlist.successBody")}
                  </p>

                  {/* Share block */}
                  {entry?.referral_code && (
                    <div className="w-full mt-4 rounded-2xl border border-forest/15 bg-canvas p-5" data-testid="share-block">
                      <div className="label-eyebrow">
                        {entry.is_founder ? t("waitlist.bringFriend") : t("waitlist.inviteLink")}
                      </div>
                      <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <div
                          className="flex-1 rounded-xl border border-forest/15 bg-white px-4 py-3 font-mono text-sm text-forest truncate"
                          data-testid="share-link-value"
                        >
                          {shareLink}
                        </div>
                        <button
                          type="button"
                          onClick={copyLink}
                          className="btn-primary sm:w-auto justify-center"
                          data-testid="share-copy-btn"
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          {copied ? t("waitlist.copied") : t("waitlist.copy")}
                        </button>
                        <button
                          type="button"
                          onClick={nativeShare}
                          className="btn-ghost sm:w-auto justify-center"
                          data-testid="share-native-btn"
                        >
                          <Share2 className="w-4 h-4" />
                          {t("waitlist.share")}
                        </button>
                      </div>
                      <p className="mt-3 text-[12px] text-ink/55">
                        {t("waitlist.inviteFootnote")}
                      </p>
                    </div>
                  )}

                  {entry?.is_founder && (
                    <a
                      href={prefix(`/founders?email=${encodeURIComponent(entry.email)}`)}
                      className="btn-accent mt-2"
                      data-testid="go-to-founders-cta"
                    >
                      {t("waitlist.openFounderCircle")} <ArrowUpRight className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field
                      testId="waitlist-name"
                      label={t("waitlist.fullName")}
                      value={name}
                      onChange={setName}
                      placeholder="Phil-Kholynes Chidi"
                    />
                    <Field
                      testId="waitlist-email"
                      label={t("waitlist.email")}
                      value={email}
                      onChange={setEmail}
                      placeholder={t("waitlist.emailPlaceholder")}
                      type="email"
                      required
                    />
                  </div>
                  <div className="mt-4">
                    <div className="label-eyebrow mb-2">{t("waitlist.corridor")}</div>
                    <div className="flex flex-wrap gap-2" data-testid="waitlist-corridor-group">
                      {["UK → NG", "US → GH", "EU → KE", "CA → ZA", "AU → UG", "Other"].map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setCorridor(c)}
                          data-testid={`corridor-${c.replace(/[^a-zA-Z]+/g, "-").replace(/^-|-$/g, "").toLowerCase()}`}
                          className={`px-3.5 py-2 rounded-full text-sm border transition-colors ${
                            corridor === c
                              ? "bg-forest text-canvas border-forest"
                              : "bg-canvas text-ink/70 border-forest/15 hover:bg-forest/5"
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>

                  {error && (
                    <div className="mt-4 text-sm text-destructive" data-testid="waitlist-error">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn-accent mt-6 w-full justify-center"
                    disabled={status === "loading"}
                    data-testid="waitlist-submit-btn"
                  >
                    {status === "loading" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>{t("waitlist.submit")} <ArrowUpRight className="w-4 h-4" /></>
                    )}
                  </button>

                  <div className="mt-4 text-[11px] text-ink/50">
                    {t("waitlist.agreeTerms")}{" "}
                    <a href={prefix("/legal/terms")} className="underline">{t("waitlist.terms")}</a> {t("waitlist.and")}{" "}
                    <a href={prefix("/legal/privacy")} className="underline">{t("waitlist.privacy")}</a>{t("waitlist.noSpam")}
                  </div>
                </>
              )}
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required, testId }) {
  return (
    <label className="block">
      <span className="label-eyebrow">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="mt-2 w-full rounded-xl border border-forest/15 bg-canvas focus:bg-white focus:border-forest px-4 py-3 text-ink placeholder-ink/40 outline-none transition-colors"
      />
    </label>
  );
}
